/** Glucera home — unified blue palette (#737DFE family), no traffic-light fills. */

export const HOME_BLUE = "#737DFE";
export const HOME_BLUE_MID = "#6268e8";
export const HOME_BLUE_DEEP = "#5258d4";

/** Chart / metric fills on Trends + Home */
export const HOME_BLUE_CHART = HOME_BLUE;
export const HOME_BLUE_CHART_MID = HOME_BLUE_MID;
export const HOME_BLUE_CHART_DEEP = HOME_BLUE_DEEP;

export const homeSectionBadge = "bg-[#737DFE]";
export const homeTrendUpBadge =
  "bg-[#737DFE]/10 text-[#737DFE] dark:bg-[#737DFE]/15 dark:text-[#737DFE]";
export const homeMetricIconPrimary = "text-[#737DFE]";
export const homeMetricIconMid = "text-[#6268e8]";
export const homeMetricIconDeep = "text-[#5258d4]";
export const homeProgressFillPrimary = "bg-[#737DFE]";
export const homeProgressFillMid = "bg-[#6268e8]";
export const homeProgressFillDeep = "bg-[#5258d4]";
export const homeValuePositive = "text-[#737DFE] dark:text-[#737DFE]";
export const homeSyncButton =
  "border-[#737DFE]/30 bg-[#737DFE]/5 hover:bg-[#737DFE]/10 text-[#737DFE]";

/** Shared date picker — same on Home, Meals, Measure */
export const gluceraCalendarTrigger =
  "flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-card hover:bg-muted";
export const gluceraCalendarIcon = "h-5 w-5 text-[#737DFE]";
export const gluceraCalendarDaySelected =
  "bg-[#737DFE] text-white hover:bg-[#6268e8]";
export const gluceraCalendarDayToday =
  "bg-[#737DFE]/10 text-[#737DFE] hover:bg-[#737DFE]/20";
export const gluceraCalendarDayFuture =
  "cursor-not-allowed text-muted-foreground hover:bg-transparent";

export type HomeScoreBand = "excellent" | "good" | "caution" | "poor";

export function resolveHomeScoreBand(label?: string, score?: number): HomeScoreBand {
  const lower = (label ?? "").toLowerCase();
  if (lower.includes("excellent")) return "excellent";
  if (lower.includes("good")) return "good";
  if (
    lower.includes("fair") ||
    lower.includes("caution") ||
    lower.includes("moderate") ||
    lower.includes("improvement")
  ) {
    return "caution";
  }
  if (lower.includes("poor") || lower.includes("needs")) return "poor";
  if (score != null) {
    if (score >= 80) return "excellent";
    if (score >= 60) return "good";
    if (score >= 40) return "caution";
    return "poor";
  }
  return "poor";
}

export function homeLabelBadgeClass(label?: string, score?: number): string {
  const band = resolveHomeScoreBand(label, score);
  switch (band) {
    case "excellent":
      return "bg-[#737DFE] text-white border border-[#737DFE]";
    case "good":
      return "bg-[#737DFE]/15 text-[#737DFE] border border-[#737DFE]/30 dark:bg-[#737DFE]/20 dark:border-[#737DFE]/35";
    case "caution":
      return "bg-[#737DFE]/10 text-[#6268e8] border border-[#737DFE]/25 dark:bg-[#737DFE]/15 dark:text-[#737DFE] dark:border-[#737DFE]/30";
    case "poor":
      return "bg-[#737DFE]/8 text-[#5258d4] border border-[#737DFE]/20 dark:bg-[#737DFE]/12 dark:text-[#6268e8] dark:border-[#737DFE]/25";
  }
}

export function homeDonutStrokeClass(label?: string, score?: number): string {
  const band = resolveHomeScoreBand(label, score);
  if (band === "excellent" || band === "good") return "stroke-[#737DFE]";
  if (band === "caution") return "stroke-[#6268e8]";
  return "stroke-[#5258d4]";
}

export function homeDonutTrackClass(): string {
  return "stroke-[#737DFE]/15 dark:stroke-[#737DFE]/20";
}

export function homeScoreTextClass(label?: string, score?: number): string {
  const band = resolveHomeScoreBand(label, score);
  if (band === "excellent" || band === "good") return "text-[#737DFE]";
  if (band === "caution") return "text-[#6268e8] dark:text-[#737DFE]";
  return "text-[#5258d4] dark:text-[#6268e8]";
}

export function homeProgressBarClass(score: number): string {
  if (score >= 80) return "bg-[#737DFE]";
  if (score >= 60) return "bg-[#737DFE]/85";
  if (score >= 40) return "bg-[#6268e8]";
  return "bg-[#5258d4]";
}

export function homePillarScoreStatus(score: number) {
  const value = Math.round(score);
  const band = resolveHomeScoreBand(undefined, value);
  const labels: Record<HomeScoreBand, string> = {
    excellent: "Excellent",
    good: "Good",
    caution: "Caution",
    poor: "Poor",
  };
  return {
    label: labels[band],
    band,
    textClass: homeScoreTextClass(undefined, value),
    barClass: homeProgressBarClass(value),
  };
}

export function homeInsightBadgeClass(badge?: string): string {
  if (!badge) return "bg-muted text-muted-foreground";
  return homeLabelBadgeClass(badge);
}

export const homeAccentText = "text-[#737DFE]";
export const homeAccentLink =
  "text-[#737DFE] transition-colors hover:text-[#6268e8] dark:hover:text-[#737DFE]";
export const homeAccentChip =
  "bg-[#737DFE]/10 text-[#737DFE] ring-1 ring-[#737DFE]/25 text-compact-sm font-bold uppercase tracking-wide dark:bg-[#737DFE]/15 dark:ring-[#737DFE]/30";
export const homeAccentIconShell =
  "bg-[#737DFE]/10 text-[#737DFE] dark:bg-[#737DFE]/15";
export const homeLowestHighlight = "bg-[#737DFE]/8 dark:bg-[#737DFE]/12";
export const homeLowestBadge =
  "bg-[#737DFE]/15 text-[#737DFE] text-compact-sm font-bold uppercase tracking-wide dark:bg-[#737DFE]/20";
export const homeCardTopWash =
  "from-[#737DFE]/[0.07] to-transparent dark:from-[#737DFE]/[0.12]";
export const homeStreakBadge =
  "border-[#737DFE]/25 bg-[#737DFE]/10 text-[#6268e8] text-compact-sm font-bold uppercase tracking-wide dark:border-[#737DFE]/30 dark:bg-[#737DFE]/15 dark:text-[#737DFE]";
export const homeTipsShell =
  "rounded-xl border border-[#737DFE]/20 bg-[#737DFE]/8 dark:border-[#737DFE]/25 dark:bg-[#737DFE]/12";
export const homeTipsLabel = "text-[#6268e8] dark:text-[#737DFE]";
export const homeTipsBody = "text-foreground/90 dark:text-foreground/85";

export function homeMealBbsTone(label?: string) {
  const band = resolveHomeScoreBand(label);
  const stroke = homeDonutStrokeClass(label);
  const track = homeDonutTrackClass();
  const score = homeScoreTextClass(label);

  if (band === "excellent" || band === "good") {
    return {
      shell:
        "border-[#737DFE]/25 bg-[#737DFE]/8 dark:border-[#737DFE]/30 dark:bg-[#737DFE]/12",
      stroke,
      track,
      score,
      badge: "bg-[#737DFE] text-white",
    };
  }
  if (band === "caution") {
    return {
      shell:
        "border-[#737DFE]/20 bg-[#737DFE]/6 dark:border-[#737DFE]/25 dark:bg-[#737DFE]/10",
      stroke,
      track,
      score,
      badge: "bg-[#6268e8] text-white",
    };
  }
  return {
    shell:
      "border-[#737DFE]/15 bg-[#737DFE]/5 dark:border-[#737DFE]/20 dark:bg-[#737DFE]/8",
    stroke,
    track,
    score,
    badge: "bg-[#5258d4] text-white",
  };
}
